/*
  Full-screen fan photo viewer. Builds on the theme's ModalDialog (global.js), which already closes on
  the X button and Esc, and keeps focus inside while open.

  On top of that:
  - a tap on the empty space around the photo (not the photo or caption) closes it too
  - swipe (mobile) or the arrow buttons (desktop) move between photos, via horizontal scroll-snap
  - the Android/iOS back button also closes it, by pushing one history entry while open
*/
if (!customElements.get('fan-photo-viewer')) {
  customElements.define(
    'fan-photo-viewer',
    class FanPhotoViewer extends ModalDialog {
      constructor() {
        super();
        this.track = this.querySelector('[data-viewer-track]');
        this.slides = Array.from(this.querySelectorAll('[data-viewer-slide]'));
        this.prevButton = this.querySelector('[data-viewer-prev]');
        this.nextButton = this.querySelector('[data-viewer-next]');
        this.currentIndex = 0;
        this.historyPushed = false;

        this.prevButton?.addEventListener('click', () => this.go(-1));
        this.nextButton?.addEventListener('click', () => this.go(1));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(-1);
          if (event.key === 'ArrowRight') this.go(1);
        });
        this.track?.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        // Only the slide's own background (the empty space around the photo) closes on click, not the
        // photo or caption -- this fires for touch taps too, unlike ModalDialog's own "media-modal" mode.
        this.addEventListener('click', (event) => {
          if (event.target.matches('.fan-photo-viewer__content, .fan-photo-viewer__track, .fan-photo-viewer__slide')) {
            this.hide();
          }
        });

        this.onPopState = () => {
          this.historyPushed = false;
          window.removeEventListener('popstate', this.onPopState);
          super.hide();
        };
      }

      show(opener, index = 0) {
        this.currentIndex = index;
        this.scrollToIndex(index, false);
        this.updateNavButtons(index);
        super.show(opener);
        window.history.pushState({ fanPhotoViewer: true }, '', window.location.href);
        this.historyPushed = true;
        window.addEventListener('popstate', this.onPopState);
      }

      hide() {
        const shouldPop = this.historyPushed;
        this.historyPushed = false;
        window.removeEventListener('popstate', this.onPopState);
        super.hide();
        if (shouldPop) window.history.back();
      }

      go(direction) {
        const next = this.currentIndex + direction;
        if (next < 0 || next >= this.slides.length) return;
        this.currentIndex = next;
        this.scrollToIndex(next, true);
        this.updateNavButtons(next);
      }

      scrollToIndex(index, smooth) {
        const slide = this.slides[index];
        if (!slide || !this.track) return;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      }

      onScroll() {
        if (!this.track) return;
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          const scrollLeft = this.track.scrollLeft;
          let closest = 0;
          let closestDistance = Infinity;
          this.slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetLeft - scrollLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = index;
            }
          });
          this.currentIndex = closest;
          this.updateNavButtons(closest);
        }, 100);
      }

      updateNavButtons(index) {
        if (this.prevButton) this.prevButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index >= this.slides.length - 1;
      }

      // Called by fan-gallery-loader after it moves a <template> batch of slides into the track, so
      // prev/next range over the newly revealed slides too.
      refreshSlides() {
        this.slides = Array.from(this.querySelectorAll('[data-viewer-slide]'));
        this.updateNavButtons(this.currentIndex);
      }
    }
  );
}

if (!customElements.get('fan-photo-trigger')) {
  customElements.define(
    'fan-photo-trigger',
    class FanPhotoTrigger extends HTMLElement {
      constructor() {
        super();
        const button = this.querySelector('button');
        if (!button) return;
        button.addEventListener('click', () => {
          const viewer = document.querySelector(this.dataset.modal);
          if (viewer) viewer.show(button, Number(this.dataset.index));
        });
      }
    }
  );
}

/*
  Fan gallery "load more": reveals one <template data-fan-photo-batch> (and its matching
  data-viewer-batch) per tap by moving its inert content into the live grid/viewer -- no network
  request, so it's instant and works with hundreds of entries without loading them up front. Once no
  batches are left, swaps in whatever <template data-fan-gallery-final> the section provided (a "View
  all" link, on the homepage teaser) or just removes the button (on the full gallery page).

  Used by both fan-gallery.liquid (homepage teaser) and main-fan-gallery.liquid (full page); which one
  it is, and what the button says at each step, is entirely decided by the Liquid that renders this
  element -- this script only knows how to reveal the next batch and swap the button area.
*/
if (!customElements.get('fan-gallery-loader')) {
  customElements.define(
    'fan-gallery-loader',
    class FanGalleryLoader extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.list = this.querySelector('[data-fan-photo-list]');
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-load-more]')) this.revealNextBatch();
        });
      }

      // The viewer moves itself to <body> on connect (ModalDialog does this for every instance), so by
      // the time a batch needs revealing it's no longer inside this element -- look it up by its stable,
      // section-scoped id instead of relying on containment.
      get viewer() {
        return document.getElementById(`FanPhotoViewer-${this.dataset.sectionId}`);
      }

      revealNextBatch() {
        const gridBatch = this.querySelector('template[data-fan-photo-batch]');
        if (gridBatch && this.list) {
          const firstNewItem = gridBatch.content.firstElementChild;
          this.list.appendChild(gridBatch.content);
          gridBatch.remove();
          firstNewItem?.querySelector('button')?.focus({ preventScroll: true });
        }

        const viewer = this.viewer;
        const track = viewer?.querySelector('[data-viewer-track]');
        const viewerBatch = viewer?.querySelector('template[data-viewer-batch]');
        if (viewerBatch && track) {
          track.appendChild(viewerBatch.content);
          viewerBatch.remove();
          viewer.refreshSlides?.();
        }

        if (!this.querySelector('template[data-fan-photo-batch]')) {
          const finalTemplate = this.querySelector('template[data-fan-gallery-final]');
          const moreWrap = this.querySelector('[data-fan-gallery-more]');
          if (finalTemplate) {
            moreWrap?.replaceWith(finalTemplate.content);
            finalTemplate.remove();
          } else {
            moreWrap?.remove();
          }
        }
      }
    }
  );
}
